import React from 'react';
import dayjs from 'dayjs';
import { Button, EllipsisSpan, Tag } from '@maxtropy/components';
import { Space } from 'antd';
import { V2EnergyConsumptionPlanGasSupplyScheduleStationPostResponse } from '@maxtropy/device-customer-apis-v2';
import { GasScheduleApiRow, ValveStatus } from '../types';
import { getHighlightCellClass, valveStatusTagColorMap, valveStatusTextMap } from '../utils';

export type GasScheduleView = 'station' | 'area';

type StationScheduleItem = Exclude<
  V2EnergyConsumptionPlanGasSupplyScheduleStationPostResponse['list'],
  undefined
>[number];
type StationScheduleValve = Exclude<StationScheduleItem['valveData'], undefined>[number];
type StationScheduleArea = Exclude<StationScheduleValve['areaTimeDetailList'], undefined>[number] & {
  areaConfigId?: number;
};

export type StationScheduleRow = Pick<StationScheduleItem, 'scadaId' | 'scadaName'> &
  Omit<StationScheduleValve, 'id' | 'areaTimeDetailList'> & {
    rowKey: string;
    stationGroupKey: string;
    valveGroupKey: string;
    valveId?: number;
    areaId?: number;
    areaConfigId?: number;
    id?: number;
    zoneName?: string;
    areaTimeDetail?: StationScheduleArea;
    valveStatus?: ValveStatus;
    inUseTime?: boolean;
    isValveStatusAbnormal?: boolean;
  };

export type ScheduleActionRow = Pick<
  GasScheduleApiRow,
  'id' | 'mediumId' | 'deviceId' | 'deviceName' | 'valveStatus' | 'inUseTime' | 'isValveStatusAbnormal'
> & {
  areaId?: number;
  areaConfigId?: number;
};

export const getScheduleActionAreaId = (record: ScheduleActionRow) => record.areaId ?? record.id;

type HighlightStyles = {
  cellDanger: string;
  cellWarning: string;
};

export function getGasScheduleRowKey(record: GasScheduleApiRow) {
  return `${record.id}-${record.mediumId}-${record.deviceId}`;
}

export function buildStationScheduleRows(
  stationList?: V2EnergyConsumptionPlanGasSupplyScheduleStationPostResponse['list']
) {
  return (stationList ?? []).flatMap((stationRecord, stationIndex) => {
    const stationGroupKey = String(stationRecord.scadaId ?? stationRecord.scadaName ?? stationIndex);

    return (stationRecord.valveData ?? []).flatMap((valveRecord, valveIndex) => {
      const { areaTimeDetailList, id: valveId, ...valveData } = valveRecord;
      const valveGroupKey = `${stationGroupKey}-${valveData.deviceId ?? valveId ?? valveData.deviceName ?? valveIndex}`;

      return ((areaTimeDetailList ?? []) as StationScheduleArea[]).map((areaRecord, areaIndex) => ({
        scadaId: stationRecord.scadaId,
        scadaName: stationRecord.scadaName,
        ...valveData,
        rowKey: `${stationIndex}-${valveIndex}-${areaRecord.id ?? 'area'}-${areaIndex}`,
        stationGroupKey,
        valveGroupKey,
        valveId,
        areaId: areaRecord.id,
        areaConfigId: areaRecord.areaConfigId,
        id: areaRecord.id,
        zoneName: areaRecord.zoneName,
        areaTimeDetail: areaRecord,
        valveStatus: areaRecord.valveStatus as ValveStatus | undefined,
        inUseTime: areaRecord.inUseTime,
        isValveStatusAbnormal: areaRecord.isValveStatusAbnormal,
      }));
    });
  });
}

export function buildAreaRowSpanMap(rows?: GasScheduleApiRow[]) {
  const rowSpanMap = new Map<string, number>();
  let activeAreaId: number | undefined;
  let activeAreaStartIndex = 0;
  let activeAreaRowKey = '';

  (rows ?? []).forEach((record, rowIndex) => {
    const rowKey = getGasScheduleRowKey(record);

    if (record.id !== activeAreaId) {
      activeAreaId = record.id;
      activeAreaStartIndex = rowIndex;
      activeAreaRowKey = rowKey;
      rowSpanMap.set(rowKey, 1);
      return;
    }

    rowSpanMap.set(activeAreaRowKey, rowIndex - activeAreaStartIndex + 1);
    rowSpanMap.set(rowKey, 0);
  });

  return rowSpanMap;
}

export function buildStationRowSpanMap(rows: StationScheduleRow[]) {
  const rowSpanMap = new Map<string, number>();
  let startIndex = 0;

  while (startIndex < rows.length) {
    const currentStationGroupKey = rows[startIndex].stationGroupKey;
    let endIndex = startIndex + 1;
    while (endIndex < rows.length && rows[endIndex].stationGroupKey === currentStationGroupKey) {
      endIndex += 1;
    }

    rowSpanMap.set(rows[startIndex].rowKey, endIndex - startIndex);
    for (let index = startIndex + 1; index < endIndex; index += 1) {
      rowSpanMap.set(rows[index].rowKey, 0);
    }
    startIndex = endIndex;
  }

  return rowSpanMap;
}

export function buildStationValveRowSpanMap(rows: StationScheduleRow[]) {
  const rowSpanMap = new Map<string, number>();
  let startIndex = 0;

  while (startIndex < rows.length) {
    const currentValveGroupKey = rows[startIndex].valveGroupKey;
    let endIndex = startIndex + 1;
    while (endIndex < rows.length && rows[endIndex].valveGroupKey === currentValveGroupKey) {
      endIndex += 1;
    }

    rowSpanMap.set(rows[startIndex].rowKey, endIndex - startIndex);
    for (let index = startIndex + 1; index < endIndex; index += 1) {
      rowSpanMap.set(rows[index].rowKey, 0);
    }
    startIndex = endIndex;
  }

  return rowSpanMap;
}

const formatTimeDetailList = (timeDetailList?: { startDateTime?: string; endDateTime?: string }[]) => {
  if (!timeDetailList?.length) return '无用能申请';

  return timeDetailList
    .map(timeDetail => {
      const start = timeDetail.startDateTime ? dayjs(timeDetail.startDateTime).format('HH:mm') : '--';
      const end = timeDetail.endDateTime ? dayjs(timeDetail.endDateTime).format('HH:mm') : '--';

      return `${start}~${end}`;
    })
    .join('、');
};

const createValveStatusColumn = <T extends ScheduleActionRow>(highlightStyles: HighlightStyles) => ({
  title: '当前阀门状态',
  dataIndex: 'valveStatus',
  width: 120,
  onCell: (record: T) => ({
    className: getHighlightCellClass(record as GasScheduleApiRow, highlightStyles),
  }),
  render: (value?: ValveStatus) => {
    if (!value) {
      return '--';
    }
    return (
      <Tag color={valveStatusTagColorMap[value]} border="solid">
        {valveStatusTextMap[value]}
      </Tag>
    );
  },
});

const createValveOperationColumn = <T extends ScheduleActionRow>(
  onOpenValveActionConfirm: (record: T, nextValveStatus: Exclude<ValveStatus, ValveStatus.Offline>) => void,
  shouldDisable?: (record: T) => boolean
) => ({
  title: '阀门操作',
  key: 'valveOperation',
  dataIndex: 'operation',
  width: 90,
  fixed: 'right' as const,
  render: (_: unknown, record: T) => {
    const currentValveStatus =
      record.valveStatus === ValveStatus.Open ||
      record.valveStatus === ValveStatus.Close ||
      record.valveStatus === ValveStatus.Offline
        ? record.valveStatus
        : undefined;
    const disableAction =
      currentValveStatus === undefined || currentValveStatus === ValveStatus.Offline || shouldDisable?.(record);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          type="link"
          disabled={disableAction || currentValveStatus === ValveStatus.Open}
          onClick={event => {
            event.stopPropagation();
            onOpenValveActionConfirm(record, ValveStatus.Open);
          }}
        >
          开
        </Button>
        <Button
          type="link"
          disabled={disableAction || currentValveStatus === ValveStatus.Close}
          onClick={event => {
            event.stopPropagation();
            onOpenValveActionConfirm(record, ValveStatus.Close);
          }}
        >
          关
        </Button>
      </div>
    );
  },
});

const createActionColumn = <T extends ScheduleActionRow>(
  onOpenRecord: (record: T) => void,
  onOpenValveConfigAutoControl: (record: T) => void,
  getValveConfigAreaId: (record: T) => number | undefined = record => record.id
) => ({
  title: '操作',
  key: 'action',
  dataIndex: 'operation',
  width: 160,
  fixed: 'right' as const,
  render: (_: unknown, record: T) => (
    <Space size={16}>
      <Button
        type="link"
        disabled={!record.deviceId}
        onClick={event => {
          event.stopPropagation();
          onOpenRecord(record);
        }}
      >
        开关记录
      </Button>
      <Button
        type="link"
        disabled={!getValveConfigAreaId(record) || !record.deviceId}
        onClick={event => {
          event.stopPropagation();
          onOpenValveConfigAutoControl(record);
        }}
      >
        自动控制配置
      </Button>
    </Space>
  ),
});

export function createAreaGasScheduleColumns(params: {
  areaRowSpanMap: Map<string, number>;
  highlightStyles: HighlightStyles;
  timeListClassName: string;
  onOpenRecord: (record: GasScheduleApiRow) => void;
  onOpenValveActionConfirm: (
    record: GasScheduleApiRow,
    nextValveStatus: Exclude<ValveStatus, ValveStatus.Offline>
  ) => void;
  onOpenValveConfigAutoControl: (record: GasScheduleApiRow) => void;
}) {
  const {
    areaRowSpanMap,
    highlightStyles,
    timeListClassName,
    onOpenRecord,
    onOpenValveActionConfirm,
    onOpenValveConfigAutoControl,
  } = params;

  return [
    {
      title: '供气区域',
      dataIndex: 'zoneName',
      width: 180,
      onCell: (record: GasScheduleApiRow) => ({
        rowSpan: areaRowSpanMap.get(getGasScheduleRowKey(record)) ?? 1,
      }),
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '供气管道阀门',
      dataIndex: 'deviceName',
      width: 260,
      onCell: (record: GasScheduleApiRow) => ({
        className: getHighlightCellClass(record, highlightStyles),
      }),
      render: (_: string, record: GasScheduleApiRow) => (
        <>
          <Tag>{record.mediumName ?? '--'}</Tag>
          <span>{record.deviceName ?? '--'}</span>
        </>
      ),
    },
    {
      title: '供气时间',
      dataIndex: 'timeDetailVoList',
      width: 260,
      onCell: (record: GasScheduleApiRow) => ({
        className: getHighlightCellClass(record, highlightStyles),
      }),
      render: (_: unknown, record: GasScheduleApiRow) => (
        <div className={timeListClassName}>{formatTimeDetailList(record.timeDetailVoList)}</div>
      ),
    },
    createValveStatusColumn<GasScheduleApiRow>(highlightStyles),
    {
      title: '阀门组态',
      dataIndex: 'scadaName',
      width: 160,
      onCell: (record: GasScheduleApiRow) => ({
        className: getHighlightCellClass(record, highlightStyles),
      }),
      render: (value: string, record: GasScheduleApiRow) => (
        <Button
          type="link"
          disabled={!record.scadaId}
          onClick={() => window.open(`/station/scada/building?stationId=${record.scadaId}`, '_blank')}
        >
          {value ?? '--'}
        </Button>
      ),
    },
    createValveOperationColumn<GasScheduleApiRow>(onOpenValveActionConfirm),
    createActionColumn<GasScheduleApiRow>(onOpenRecord, onOpenValveConfigAutoControl),
  ];
}

export function createStationGasScheduleColumns(params: {
  stationRowSpanMap: Map<string, number>;
  stationValveRowSpanMap: Map<string, number>;
  highlightStyles: HighlightStyles;
  timeListClassName: string;
  onOpenRecord: (record: StationScheduleRow) => void;
  onOpenValveActionConfirm: (
    record: StationScheduleRow,
    nextValveStatus: Exclude<ValveStatus, ValveStatus.Offline>
  ) => void;
  onOpenValveConfigAutoControl: (record: StationScheduleRow) => void;
}) {
  const {
    stationRowSpanMap,
    stationValveRowSpanMap,
    highlightStyles,
    timeListClassName,
    onOpenRecord,
    onOpenValveActionConfirm,
    onOpenValveConfigAutoControl,
  } = params;

  return [
    {
      title: '站房',
      dataIndex: 'scadaName',
      width: 180,
      onCell: (record: StationScheduleRow) => ({
        rowSpan: stationRowSpanMap.get(record.rowKey) ?? 1,
      }),
      render: (value: string, record: StationScheduleRow) => (
        <Button
          type="link"
          disabled={!record.scadaId}
          onClick={() => window.open(`/station/scada/building?stationId=${record.scadaId}`, '_blank')}
        >
          {value ?? '--'}
        </Button>
      ),
    },
    {
      title: '供气管道阀门',
      dataIndex: 'deviceName',
      width: 200,
      onCell: (record: StationScheduleRow) => ({
        rowSpan: stationValveRowSpanMap.get(record.rowKey) ?? 1,
        className: getHighlightCellClass(record as GasScheduleApiRow, highlightStyles),
      }),
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '管道供气时间',
      dataIndex: 'pipetimeDetailVoList',
      width: 220,
      onCell: (record: StationScheduleRow) => ({
        rowSpan: stationValveRowSpanMap.get(record.rowKey) ?? 1,
        className: getHighlightCellClass(record as GasScheduleApiRow, highlightStyles),
      }),
      render: (_: unknown, record: StationScheduleRow) => (
        <div className={timeListClassName}>{formatTimeDetailList(record.pipetimeDetailVoList)}</div>
      ),
    },
    {
      title: '供气区域',
      dataIndex: 'zoneName',
      width: 160,
      onCell: (record: StationScheduleRow) => ({
        className: getHighlightCellClass(record as GasScheduleApiRow, highlightStyles),
      }),
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '区域供气时间',
      dataIndex: 'areaTimeDetail',
      width: 220,
      onCell: (record: StationScheduleRow) => ({
        className: getHighlightCellClass(record as GasScheduleApiRow, highlightStyles),
      }),
      render: (_: unknown, record: StationScheduleRow) => (
        <div className={timeListClassName}>{formatTimeDetailList(record.areaTimeDetail?.areatimeDetailVoList)}</div>
      ),
    },
    createValveStatusColumn<StationScheduleRow>(highlightStyles),
    createValveOperationColumn<StationScheduleRow>(
      onOpenValveActionConfirm,
      record => !getScheduleActionAreaId(record)
    ),
    createActionColumn<StationScheduleRow>(onOpenRecord, onOpenValveConfigAutoControl, record => record.areaConfigId),
  ];
}
